import type React from 'react';
import { ME_TOKEN, type FilterGroup } from '@shared/flow/filter';
import type { FilterFieldCatalogEntry } from '@shared/flow/filter-fields';
import type { FlowIdentity } from '@shared/flow/identity';
import type { FlowRole } from '@shared/flow/role';

/**
 * The `{me}` empty state (U5 / R2 — "empty states name the cause").
 *
 * Deleting `scope` made every narrowing visible, but a VISIBLE narrowing can
 * still be baffling: a rep who isn't the ClayDR on anything opens a
 * `ClayDR is [Me]` view and gets zero rows. A bare "No results." leaves them
 * guessing, which is the same dead end the hidden scope param used to create.
 * So when a query comes back empty AND the active filter carries the `{me}`
 * token, the table says which filter did it and offers the undo.
 *
 * Everything here is PURE apart from the returned node — no hooks — so the
 * table shells can call it inline during render and hand the result straight
 * to `FlowTable`'s existing `emptyState` prop.
 *
 * U6 SEAM: `creatorName` is the only piece this unit can't supply, because no
 * saved views exist yet — a view's creator is a U6 concept. Passing it in
 * turns on the doc's second affordance ("[Show Jake Smith's instead]", which
 * swaps the token for that literal name). Until then the state reduces to
 * [Clear] alone, and `withMeConditionsResolvedTo` sits here fully tested,
 * waiting for its caller.
 */

/** Which flow roles can plausibly hold each person field (see roleDefaults.ts). */
const FIELD_HOLDER_ROLES: Record<string, ReadonlyArray<FlowRole>> = {
  claydr_name: ['claydr'],
  // A GS can also be an account's SFDC owner, so owner_name/account_owner_name
  // are treated as held by gs too — a deliberate product judgement wider than
  // ROLE_ME_CHIP (which seeds gs only gs_name); do not derive this table from
  // the chip model.
  owner_name: ['gtme', 'gs'],
  account_owner_name: ['gtme', 'gs'],
  account_claydr_name: ['claydr'],
  gs_name: ['gs'],
};

/** Human noun for the role that holds a person field, used in the copy. */
const FIELD_ROLE_NOUN: Record<string, string> = {
  claydr_name: 'ClayDR',
  owner_name: 'GTME or Growth Strategist',
  account_owner_name: 'GTME or Growth Strategist',
  account_claydr_name: 'ClayDR',
  gs_name: 'Growth Strategist',
};

function isMe(value: unknown): boolean {
  return value === ME_TOKEN;
}

/** Every distinct field key carrying a `{me}` token, in first-seen order. */
export function meFilterFields(filters: FilterGroup[]): string[] {
  const seen: string[] = [];
  for (const g of filters) {
    for (const c of g.conditions) {
      if (isMe(c.value) && !seen.includes(c.field)) seen.push(c.field);
    }
  }
  return seen;
}

/**
 * Drop the `{me}` conditions — and only those. A group left with no
 * conditions is dropped too (an empty group is not a neutral filter; it's
 * noise the popover would render as a stray card).
 *
 * Deliberately NOT "clear all filters": the empty-state sentence names one
 * specific narrowing, so its button must undo exactly that one. Wiping a
 * rep's other conditions would be a second surprise on top of the first.
 */
export function withoutMeConditions(filters: FilterGroup[]): FilterGroup[] {
  return filters
    .map(g => ({ ...g, conditions: g.conditions.filter(c => !isMe(c.value)) }))
    .filter(g => g.conditions.length > 0);
}

/**
 * Swap every `{me}` token for a literal person name — the U6
 * "[Show <creator>'s instead]" action. Literal person values compare on the
 * name column (condition-to-sql's text branch), so this is a real, runnable
 * filter, not a display trick.
 */
export function withMeConditionsResolvedTo(filters: FilterGroup[], name: string): FilterGroup[] {
  return filters.map(g => ({
    ...g,
    conditions: g.conditions.map(c => (isMe(c.value) ? { ...c, value: name } : c)),
  }));
}

export interface MeFilterEmptyStateInput {
  /** The filter that produced zero rows (the live one, not the view's saved definition). */
  filters: FilterGroup[];
  /** Field catalog for label lookup — the same one the Filters popover renders. */
  filterFields: FilterFieldCatalogEntry[];
  /** Viewer identity; null when no flow role was detected. */
  identity: FlowIdentity | null;
  /** U6: the active view's creator display name, when it resolves. */
  creatorName?: string | null;
  onFiltersChange: (next: FilterGroup[]) => void;
}

/**
 * Returns the empty-state node, or null when this isn't a `{me}` empty state
 * (the caller then falls back to its own copy / FlowTable's "No results.").
 */
export function buildMeFilterEmptyState(input: MeFilterEmptyStateInput): React.ReactNode | null {
  const { filters, filterFields, identity, creatorName, onFiltersChange } = input;

  const fields = meFilterFields(filters);
  if (fields.length === 0) return null;

  const labelOf = (key: string) => filterFields.find(f => f.key === key)?.label ?? key;
  const fieldLabels = fields.map(labelOf).join(' and ');

  // Only assert a role mismatch we can actually prove. `{me}` resolves by
  // SFDC user id, independent of Terra's role detection (Decision 6), so an
  // empty result does NOT imply the viewer lacks the role — a ClayDR with an
  // empty book would be told a falsehood. Claim it only when we know the
  // viewer's role AND no `{me}` field is one that role can hold.
  const mismatchedRoleNoun = (() => {
    if (!identity) return null;
    const unheld = fields.filter(f => {
      const holders = FIELD_HOLDER_ROLES[f];
      return holders !== undefined && !holders.includes(identity.flowRole);
    });
    if (unheld.length === 0 || unheld.length !== fields.length) return null;
    return FIELD_ROLE_NOUN[unheld[0]] ?? null;
  })();

  const sentence = mismatchedRoleNoun
    ? `This view filters to ${fieldLabels} is Me and you aren't a ${mismatchedRoleNoun}. 0 rows.`
    : `This view filters to ${fieldLabels} is Me. 0 rows.`;

  return (
    <span>
      {sentence}
      <button
        type="button"
        onClick={() => onFiltersChange(withoutMeConditions(filters))}
        className="ml-1 text-content-action dark:text-blueberry-400 hover:underline"
      >
        Clear
      </button>
      {creatorName ? (
        <button
          type="button"
          onClick={() => onFiltersChange(withMeConditionsResolvedTo(filters, creatorName))}
          className="ml-2 text-content-action dark:text-blueberry-400 hover:underline"
        >
          {`Show ${creatorName}'s instead`}
        </button>
      ) : null}
    </span>
  );
}
